import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Package, LogOut, KeyRound, Eye, EyeOff, Truck, Store, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountPage = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwdData, setPwdData] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/orders`);
        setOrders(response.data);
      } catch (error) {
        // Error shown via loading=false + null data check in UI
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdData.newPwd !== pwdData.confirm) {
      toast.error(t('register_password_mismatch'));
      return;
    }
    setPwdLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: pwdData.current,
        new_password: pwdData.newPwd,
      });
      toast.success(t('change_pwd_success'));
      setShowPwdForm(false);
      setPwdData({ current: '', newPwd: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setPwdLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-500 bg-yellow-500/10',
      processing: 'text-purple-500 bg-purple-500/10',
      ready: 'text-blue-500 bg-blue-500/10',
      shipped: 'text-cyan-500 bg-cyan-500/10',
      delivered: 'text-green-500 bg-green-500/10',
      cancelled: 'text-red-500 bg-red-500/10',
      // legacy
      confirmed: 'text-blue-500 bg-blue-500/10',
    };
    return colors[status] || 'text-zinc-500 bg-zinc-500/10';
  };

  const getStatusLabel = (status) => {
    return t(`status_${status}`) || status;
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[color:var(--color-primary)] text-sm uppercase tracking-[0.3em] mb-2">{t('account_title')}</p>
            <h1 className="text-4xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('account_hello', { name: user?.name || '' })}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[color:rgb(var(--primary-rgb)/0.10)] flex items-center justify-center">
                  <User className="w-6 h-6 text-[color:var(--color-primary)]" />
                </div>
                <div>
                  <p className="text-white">{user?.name}</p>
                  <p className="text-zinc-500 text-sm">{user?.phone}</p>
                </div>
              </div>
              <nav className="space-y-2">
                <Link to="/compte" className="flex items-center gap-3 p-3 bg-[color:rgb(var(--primary-rgb)/0.10)] text-[color:var(--color-primary)]">
                  <Package className="w-4 h-4" /> {t('account_orders')}
                </Link>
                <button onClick={() => setShowPwdForm(!showPwdForm)} className="flex items-center gap-3 p-3 w-full text-left text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
                  <KeyRound className="w-4 h-4" /> {t('change_pwd_title')}
                </button>
                <button onClick={logout} className="flex items-center gap-3 p-3 w-full text-left text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
                  <LogOut className="w-4 h-4" /> {t('nav_logout')}
                </button>
              </nav>

              {showPwdForm && (
                <form onSubmit={handleChangePassword} className="mt-4 space-y-3 border-t border-white/10 pt-4" data-testid="change-pwd-form">
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-wider mb-1 block">{t('change_pwd_current')}</label>
                    <input type={showPwd ? 'text' : 'password'} value={pwdData.current} onChange={e => setPwdData({...pwdData, current: e.target.value})} required className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)]" data-testid="current-pwd" />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-wider mb-1 block">{t('change_pwd_new')}</label>
                    <input type={showPwd ? 'text' : 'password'} value={pwdData.newPwd} onChange={e => setPwdData({...pwdData, newPwd: e.target.value})} required minLength={6} className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)]" data-testid="new-pwd" />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-wider mb-1 block">{t('change_pwd_confirm')}</label>
                    <input type={showPwd ? 'text' : 'password'} value={pwdData.confirm} onChange={e => setPwdData({...pwdData, confirm: e.target.value})} required minLength={6} className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)]" data-testid="confirm-pwd" />
                  </div>
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="text-zinc-500 text-xs hover:text-white flex items-center gap-1">
                    {showPwd ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {showPwd ? 'Masquer' : 'Afficher'}
                  </button>
                  <button type="submit" disabled={pwdLoading} className="w-full bg-[color:var(--color-primary)] text-black text-sm py-2 hover:bg-[color:var(--color-primary-hover)] transition-colors disabled:opacity-50" data-testid="submit-pwd">
                    {pwdLoading ? '...' : t('change_pwd_save')}
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
              <h2 className="text-2xl text-white font-light mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('account_order_history')}
              </h2>

              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => <div key={`skeleton-${i}`} className="bg-white/5 h-24 animate-pulse" />)}
                </div>
              ) : orders.length > 0 ? (
                <div className="space-y-4" data-testid="orders-list">
                  {orders.map(order => (
                    <div key={order.id} className="border border-white/10 p-4" data-testid={`order-${order.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                        <div>
                          <p className="text-white text-sm">{t('account_order_prefix')} #{order.id.slice(-8).toUpperCase()}</p>
                          <p className="text-zinc-500 text-xs">
                            {new Date(order.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 text-xs uppercase tracking-wider ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                          <span className="text-[color:var(--color-primary)] font-medium">{order.total.toFixed(2)}€</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {order.items.slice(0, 3).map((item, idx) => (
                          <span key={`${item.product_name}-${idx}`} className="text-zinc-500 text-xs">
                            {item.brand && <span className="text-zinc-600">{item.brand} — </span>}{item.product_name} x{item.quantity}
                            {item.original_quantity && item.original_quantity !== item.quantity && (
                              <span className="text-amber-500"> ({t('account_ordered')}: {item.original_quantity})</span>
                            )}
                            {item.status === 'partial' && <span className="ml-1 text-amber-500">[{t('account_partial')}]</span>}
                            {item.status === 'unavailable' && <span className="ml-1 text-red-500">[{t('account_unavailable')}]</span>}
                            {idx < Math.min(order.items.length - 1, 2) && ','}
                          </span>
                        ))}
                        {order.items.length > 3 && (
                          <span className="text-zinc-600 text-xs">{t('favorites_others', { count: order.items.length - 3 })}</span>
                        )}
                      </div>
                      {order.admin_note && (
                        <div className="mt-2 p-2 border border-amber-500/20 bg-amber-500/5 text-amber-400 text-xs">
                          {t('account_note')}: {order.admin_note}
                        </div>
                      )}

                      {/* Shipment tracking — visible once the order is shipped */}
                      {(order.shipping_method === 'carrier' || order.shipping_method === 'pickup') && (
                        <div className="mt-3 p-3 border border-cyan-500/30 bg-cyan-500/5 flex items-start gap-3" data-testid={`shipment-${order.id}`}>
                          {order.shipping_method === 'pickup' ? (
                            <Store className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Truck className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="text-xs flex-1">
                            {order.shipping_method === 'pickup' ? (
                              <>
                                <p className="text-cyan-400 font-medium">{t('account_pickup_title')}</p>
                                <p className="text-zinc-400 mt-0.5">{t('account_pickup_msg')}</p>
                              </>
                            ) : (
                              <>
                                <p className="text-cyan-400">
                                  {t('account_shipped_via')} <strong>{order.carrier}</strong>
                                </p>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="text-zinc-400">{t('account_tracking_number')}:</span>
                                  <code className="text-zinc-200 bg-white/5 px-1.5 py-0.5">{order.tracking_number}</code>
                                  {order.tracking_url && (
                                    <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                                      className="text-[color:var(--color-primary)] hover:underline inline-flex items-center gap-1"
                                      data-testid={`track-${order.id}`}>
                                      {t('account_track_parcel')}
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-500 mb-4">{t('account_no_orders')}</p>
                  <Link to="/catalogue" className="btn-primary">{t('account_discover')}</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
